import React, { useEffect, useMemo, useState } from "react";
import Api from "../../../services/api.js";
import moment from "moment/moment";
import { END_DATE, START_DATE } from "../../../utility/index.js";
import { useSelector } from "react-redux";
import {
  Card,
  Row,
  Col,
  Select,
  DatePicker,
  Button,
  Table,
  Typography,
  Drawer,
} from "antd";
import {
  ResponsiveContainer,
  AreaChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Area,
} from "recharts";
import dayjs from "dayjs";
import { SearchOutlined } from '@ant-design/icons';

function RevenueIAA() {
  const apps = useSelector((state) => state.app.allApps);

  const [startDate, setStartDate] = useState(
    dayjs().subtract(START_DATE, "day")
  );
  const [endDate, setEndDate] = useState(dayjs().subtract(END_DATE, "day"));

  const [dataApps, setDataApps] = useState([]);
  const [selectedApp, setSelectedApp] = useState([]);
  const [selectedAdType, setSelectedAdType] = useState("");
  const [selectedMetrics, setSelectedMetrics] = useState([]);
  const [selectCountry, setSelectCountry] = useState([]);
  const [selectedCountry, setSelectedCountry] = useState([]);
  const [selectedType, setSelectedType] = useState("");
  const [drawerOpen, setDrawerOpen] = useState(false);

  const handleChangeDate = (values) => {
    if (Array.isArray(values) && values[0] && values[1]) {
      setStartDate(values[0]);
      setEndDate(values[1]);
    }
  };

  useEffect(() => {
    const data = (apps || []).map((i) => ({
      value: `${i.code}-${i.os}`,
      label: `${i.code} - ${i.name} ${i.os}`,
      thumb: i.thumb || null,
      os: i.os || null,
    }));
    setDataApps(data);
  }, [apps]);

  const appOptions = useMemo(() => {
    return (dataApps || []).map((opt) => {
      const os = (opt.os || "").toString().toLowerCase();
      const overlaySrc =
        os === "ios"
          ? "/bg-ios.png"
          : os === "android"
          ? "/bg-android.png"
          : null;
      return {
        value: opt.value,
        label: (
          <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {opt.thumb ? (
              <span
                style={{
                  position: "relative",
                  width: 20,
                  height: 20,
                  display: "inline-block",
                }}
              >
                <img
                  src={opt.thumb}
                  alt={typeof opt.label === "string" ? opt.label : "app"}
                  style={{
                    width: 20,
                    height: 20,
                    borderRadius: 4,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
                {overlaySrc ? (
                  <img
                    src={overlaySrc}
                    alt={os}
                    style={{
                      position: "absolute",
                      inset: 0,
                      width: 20,
                      height: 20,
                      borderRadius: 4,
                      objectFit: "cover",
                      pointerEvents: "none",
                    }}
                  />
                ) : null}
              </span>
            ) : null}
            <span>
              {typeof opt.label === "string" ? opt.label : String(opt.value)}
            </span>
          </span>
        ),
      };
    });
  }, [dataApps]);

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const response = await Api.get(
          `/api/app/dimension_filter?dimension=country`
        );
        if (response?.data?.data) {
          const options = Array.isArray(response.data.data)
            ? response.data.data
            : [];
          setSelectCountry(options);
        }
      } catch (error) {
        console.error("Error fetching countries:", error);
      }
    };

    fetchCountries();
  }, []);

  const handleAppsChange = (values) => {
    setSelectedApp(values || []);
  };

  const handleChangeAdType = (value) => {
    setSelectedAdType(value);
  };
  const handleChangeType = (value) => {
    setSelectedType(value);
  };

  const handleChangeMetrics = (value) => {
    setSelectedMetrics(value || []);
  };

  const dataChart = [
    {
      name: "Page A",
      uv: 4000,
      pv: 2400,
      amt: 2400,
    },
    {
      name: "Page B",
      uv: 3000,
      pv: 1398,
      amt: 2210,
    },
    {
      name: "Page C",
      uv: 2000,
      pv: 9800,
      amt: 2290,
    },
    {
      name: "Page D",
      uv: 2780,
      pv: 3908,
      amt: 2000,
    },
    {
      name: "Page E",
      uv: 1890,
      pv: 4800,
      amt: 2181,
    },
    {
      name: "Page F",
      uv: 2390,
      pv: 3800,
      amt: 2500,
    },
    {
      name: "Page G",
      uv: 3490,
      pv: 4300,
      amt: 2100,
    },
  ];

  const { Title } = Typography;

  const rangePickerValue = useMemo(
    () => [startDate, endDate],
    [startDate, endDate]
  );

  const groupDefs = [
    { key: "google", title: "Google bidding", color: "#1677ff" },
    { key: "meta", title: "Meta", color: "#52c41a" },
    { key: "greedy", title: "Greedy", color: "#faad14" },
    { key: "netlink", title: "Netlink", color: "#13c2c2" },
    { key: "moloco", title: "Moloco", color: "#eb2f96" },
    { key: "pangle", title: "Pangle", color: "#722ed1" },
  ];

  const childDefs = [
    { key: "max", title: "MAX", width: 140, bg: "#E6F4FF" },
    { key: "network", title: "Ad network", width: 140, bg: "#FFF7E6" },
    { key: "diff", title: "% chênh lệch", width: 140, bg: "#F6FFED" },
  ];

  const networkColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      fixed: "left",
      width: 120,
      align: "center",
      render: (text, record) =>
        text === "Total / Average" ? <b>{text}</b> : text,
    },
    ...groupDefs.map((g) => ({
      title: g.title,
      onHeaderCell: () => ({
        style: {
          background: g.color,
          color: "#fff",
          fontWeight: 600,
          textAlign: "center",
        },
      }),
      children: childDefs.map((c) => ({
        title: c.title,
        dataIndex: [g.key, c.key],
        key: `${g.key}_${c.key}`,
        align: "center",
        width: c.width,
        onHeaderCell: () => ({
          style: { background: c.bg, fontWeight: 600, textAlign: "center" },
        }),
      })),
    })),
  ];

  const networkData = [
    { key: "total", date: "Total / Average" },
    { key: "2025-05-08", date: "8/5/2025" },
    { key: "2025-05-07", date: "7/5/2025" },
    { key: "2025-05-06", date: "6/5/2025" },
    { key: "2025-05-05", date: "5/5/2025" },
    { key: "2025-05-04", date: "4/5/2025" },
    { key: "2025-05-03", date: "3/5/2025" },
  ];

  const ecpmColumns = [
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      fixed: "left",
      width: 140,
      align: "center",
      render: (text) => (text === "Total / Average" ? <b>{text}</b> : text),
    },
    { title: "Reward", dataIndex: "reward", key: "reward", align: "center" },
    { title: "Inter", dataIndex: "inter", key: "inter", align: "center" },
    { title: "Banner", dataIndex: "banner", key: "banner", align: "center" },
    { title: "MREC", dataIndex: "mrec", key: "mrec", align: "center" },
    { title: "Native", dataIndex: "native", key: "native", align: "center" },
  ];

  const ecpmData = [
    {
      key: "summary",
      date: "Total / Average",
      reward: "20 (+10%)",
      inter: "20 (+10%)",
      banner: "20 (+10%)",
      mrec: "20 (+10%)",
      native: "20 (+10%)",
    },
    {
      key: "2025-08-20",
      date: "20/08/2025",
      reward: "20 (+10%)",
      inter: "20 (+10%)",
      banner: "20 (+10%)",
      mrec: "20 (+10%)",
      native: "20 (+10%)",
    },
    {
      key: "2025-08-21",
      date: "21/08/2025",
      reward: "20 (+10%)",
      inter: "20 (+10%)",
      banner: "20 (+10%)",
      mrec: "20 (+10%)",
      native: "20 (+10%)",
    },
  ];

  const adTypeOptions = [
    { value: "inter", label: "Inter" },
    { value: "reward", label: "Reward" },
    { value: "banner", label: "Banner" },
    { value: "mrec", label: "MREC" },
    { value: "aoa", label: "AOA" },
    { value: "native", label: "Native" },
  ];

  const { RangePicker } = DatePicker;

  return (
    <>
    <div className="background-second-dashboard">
      <section className="main__content-dashbard">
        <div className="container-fluid">
          <Card bodyStyle={{ padding: 20 }}>
            <div className="overview_content pd-20">
              {/* Mobile: floating search icon to open drawer */}
              <div className="mobile-search-toggle" style={{ 
                display: 'none',
                position: 'fixed',
                bottom: '20px',
                right: '20px',
                zIndex: 1000
              }}>
                <Button
                  type="primary"
                  shape="circle"
                  icon={<SearchOutlined />}
                  onClick={() => setDrawerOpen(true)}
                  style={{
                    borderRadius: '50%',
                    width: '48px',
                    height: '48px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    position: 'relative'
                  }}
                />
              </div>

              <div className="mb-4 desktop-filter">
                <Row gutter={[12, 12]}>
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div style={{ marginBottom: 4 }}>Apps:</div>
                    <Select
                      mode="multiple"
                      allowClear
                      placeholder="Select apps"
                      options={appOptions}
                      value={selectedApp}
                      onChange={handleAppsChange}
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={6} lg={4}>
                    <div style={{ marginBottom: 4 }}>Type:</div>
                    <Select
                      placeholder="Type"
                      options={[{ value: "ecpm", label: "eCPM" }]}
                      value={selectedType || undefined}
                      onChange={handleChangeType}
                      allowClear
                      style={{ width: "100%" }}
                    />
                  </Col>
                  {selectedType && (
                    <Col xs={24} sm={12} md={6} lg={4}>
                      <div style={{ marginBottom: 4 }}>Ad type:</div>
                      <Select
                        placeholder="Ad type"
                        options={adTypeOptions}
                        value={selectedAdType || undefined}
                        onChange={handleChangeAdType}
                        allowClear
                        style={{ width: "100%" }}
                      />
                    </Col>
                  )}
                  <Col xs={24} sm={12} md={8} lg={6}>
                    <div style={{ marginBottom: 4 }}>Date range:</div>
                    <RangePicker
                      value={rangePickerValue}
                      onChange={handleChangeDate}
                      style={{ width: "100%" }}
                      allowClear={false}
                    />
                  </Col>
                  <Col xs={24} sm={12} md={4} lg={4}>
                    <div style={{ marginBottom: 4 }}>&nbsp;</div>
                    <Button type="primary" block>
                      Run Report
                    </Button>
                  </Col>
                </Row>
              </div>

              <div className="mb-4">
                <div className="title">
                  <h5 className="" style={{ padding: "20px 0" }}>
                    <b>Network</b>
                  </h5>
                </div>
              </div>

              <div className="mb-4">
                <Table
                  columns={networkColumns}
                  dataSource={networkData}
                  pagination={false}
                  bordered
                  scroll={{ x: 1200 }}
                  // sticky
                />
              </div>

              {/* <div className="mb-4">
                <div className="title">
                  <h5 className="" style={{ padding: "20px 0" }}>
                    <b>eCPM Daily</b>
                  </h5>
                </div>
              </div>
              <div className="mb-10">
                <ResponsiveContainer width="100%" height={400}>
                  <AreaChart
                    width="100%"
                    height={400}
                    data={dataChart}
                    margin={{
                      top: 10,
                      right: 30,
                      left: 0,
                      bottom: 0,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Area
                      type="monotone"
                      dataKey="uv"
                      stroke="#EB4B2C"
                      fill="#FDEDEA"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="mb-4">
                <Table
                  columns={ecpmColumns}
                  dataSource={ecpmData}
                  pagination={false}
                  bordered
                  scroll={{ x: 800 }}
                  // sticky
                />
              </div> */}
            </div>
          </Card>
        </div>
      </section>
    </div>
    {/* Mobile: Drawer with filters */}
    <Drawer
      title="Search Filters"
      placement="right"
      onClose={() => setDrawerOpen(false)}
      open={drawerOpen}
      width={320}
      className="mobile-filter-drawer"
      zIndex={2500}
    >
      <Row gutter={[12, 12]}>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>Apps:</div>
          <Select
            mode="multiple"
            allowClear
            placeholder="Select apps"
            options={appOptions}
            value={selectedApp}
            onChange={handleAppsChange}
            style={{ width: "100%" }}
          />
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>Type:</div>
          <Select
            placeholder="Type"
            options={[{ value: "ecpm", label: "eCPM" }]}
            value={selectedType || undefined}
            onChange={handleChangeType}
            allowClear
            style={{ width: "100%" }}
          />
        </Col>
        {selectedType && (
          <Col xs={24}>
            <div style={{ marginBottom: 4 }}>Ad type:</div>
            <Select
              placeholder="Ad type"
              options={adTypeOptions}
              value={selectedAdType || undefined}
              onChange={handleChangeAdType}
              allowClear
              style={{ width: "100%" }}
            />
          </Col>
        )}
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>Date range:</div>
          <RangePicker
            value={rangePickerValue}
            onChange={handleChangeDate}
            style={{ width: "100%" }}
            allowClear={false}
            getPopupContainer={(trigger) => trigger.parentNode}
            dropdownClassName="drawer-date-dropdown"
          />
        </Col>
        <Col xs={24}>
          <div style={{ marginBottom: 4 }}>&nbsp;</div>
          <Button type="primary" block onClick={() => setDrawerOpen(false)}>
            Apply
          </Button>
        </Col>
      </Row>
    </Drawer>
    <style>{`
      @media (max-width: 768px) {
        .desktop-filter { display: none !important; }
        .mobile-search-toggle { display: block !important; }
        .mobile-filter-drawer .ant-drawer-body { padding: 10px !important; }
        .mobile-filter-drawer .drawer-date-dropdown { max-width: 100% !important; }
        .mobile-filter-drawer .drawer-date-dropdown .ant-picker-panels { flex-direction: column !important; }
      }
      @media (min-width: 769px) {
        .mobile-search-toggle { display: none !important; }
        .desktop-filter { display: block !important; }
      }
    `}</style>
    </>
  );
}

export default RevenueIAA;
